<script>
  import { onMount, onDestroy } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';
  import BankSelectorModal from '../components/BankSelectorModal.svelte';
  import CreditCardSelectorModal from '../components/AdvancedCreditCardSelectorModal.svelte';
  import PaymentMethodSelectorModal from '../components/PaymentMethodSelectorModal.svelte';
  import CategorySelectorModal from '../components/CategorySelectorModal.svelte';
  import StoreSelectorModal from '../components/StoreSelectorModal.svelte';
  import PlaceSelectorModal from '../components/PlaceSelectorModal.svelte';
  import { cloneStore, scanStatus } from '../cloneStore.js';
  import { get } from 'svelte/store';
  import { selectedCountry } from '../countryStore';
  import { authFetch } from '../authService';

  // --- Form State Variables ---
  let name = '';
  let description = '';
  let brand = '';
  let promoCode = '';
  let startDate = new Date().toISOString().split('T')[0];
  let endDate = '';
  
  let categories = [];
  let hyperlink = '';
  let banks = [];
  let creditCards = [];
  let methodsOfSpending = [];

  let isOnline = false;
  let onlineShops = '';
  let isInStore = false;
  let physicalStores = []; // Changed from string to array
  let places = [];
  let showStoreModal = false;
  let showPlaceModal = false;

  // Spending & Achievement Vars
  let minimumSpending = '';
  let targetNumTransactions = '';
  let minimumTargetAccumulatedSpending = '';
  let maximumTargetNumTransactions = '';
  let maximumTargetAccumulatedSpending = '';
  
  // Reward Fields
  let originalPrice = '';
  let discountPrice = '';
  let maximumReward = '';
  let maximumRewardPercentage = '';
  let eligibleDaysOfWeek = []; 
  let eligibleDates = [];
  let tempDate = ''; // Temporary holder for the date picker
  let ineligibleDates = [];
  let tempIneligibleDate = '';
  
  // Rules & Tiers
  let rewardScheme = []; // Frequency based (Every X transactions)
  let rewardTiers = [];  // Condition based (Spend > $X)

  let requireRegistration = false;
  let registrationDetail = '';
  let includeSelectedMember = false;
  let selectedMember = false;
  
  // Modal State
  let showBankModal = false;
  let showCreditCardModal = false;
  let showPaymentMethodModal = false;
  let showCategoryModal = false;

  // Submission State
  let error = '';
  let isSubmitting = false;
  let cloneFromPromo = '';

  // --- Component State ---
  let currentUser = null;
  let userProfile = null;

  // Constant for rendering days
    const daysOfWeekList = [
        { id: 0, label: 'Sunday' },
        { id: 1, label: 'Monday' },
        { id: 2, label: 'Tuesday' },
        { id: 3, label: 'Wednesday' },
        { id: 4, label: 'Thursday' },
        { id: 5, label: 'Friday' },
        { id: 6, label: 'Saturday' }
    ];

  let scanState = { isScanning: false, error: null, data: null };
  let unsubscribeClone;
  let unsubscribeScan;

  onMount(() => {
    authStore.subscribe(value => {
        currentUser = value.user;
        userProfile = value.profile;
    });
    
    unsubscribeScan = scanStatus.subscribe(val => {
        scanState = val;
    });

    unsubscribeClone = cloneStore.subscribe(dataToClone => {
        if (dataToClone) {
            const formTouched = name || description || brand || promoCode || categories.length > 0 || banks.length > 0;
            const applyData = () => {
                cloneFromPromo = dataToClone._id || dataToClone.id || '';
                name = dataToClone.name || '';
                description = dataToClone.description || '';
                brand = dataToClone.brand || '';
                promoCode = dataToClone.promoCode || '';
                startDate = dataToClone.startDate ? dataToClone.startDate.split('T')[0] : new Date().toISOString().split('T')[0];
                endDate = dataToClone.endDate ? dataToClone.endDate.split('T')[0] : '';
                categories = dataToClone.categories || [];
                hyperlink = dataToClone.hyperlink || '';
                banks = dataToClone.banks || [];
                creditCards = dataToClone.creditCards || [];
                isOnline = dataToClone.isOnline || false;
                onlineShops = dataToClone.onlineShops || '';
                isInStore = dataToClone.isInStore || false;
                physicalStores = dataToClone.physicalStores || [];
                places = dataToClone.places || [];
                minimumSpending = dataToClone.minimumSpending || '';
                methodsOfSpending = dataToClone.methodsOfSpending || [];
                targetNumTransactions = dataToClone.targetNumTransactions || '';
                minimumTargetAccumulatedSpending = dataToClone.minimumTargetAccumulatedSpending || '';
                maximumTargetNumTransactions = dataToClone.maximumTargetNumTransactions || '';
                maximumTargetAccumulatedSpending = dataToClone.maximumTargetAccumulatedSpending || '';
                requireRegistration = dataToClone.requireRegistration || false;
                registrationDetail = dataToClone.registrationDetail || '';
                includeSelectedMember = dataToClone.includeSelectedMember || false;
                selectedMember = dataToClone.selectedMember || false;
                
                originalPrice = dataToClone.originalPrice || '';
                discountPrice = dataToClone.discountPrice || '';
                maximumReward = dataToClone.maximumReward || '';
                maximumRewardPercentage = dataToClone.maximumRewardPercentage || '';
                
                rewardScheme = dataToClone.rewardScheme || [];
                rewardTiers = dataToClone.rewardTiers || [];

                eligibleDates = dataToClone.eligibleDates || [];
                ineligibleDates = dataToClone.ineligibleDates || [];
                eligibleDaysOfWeek = dataToClone.eligibleDaysOfWeek || [];
            };

            if (formTouched) {
                if (confirm("Scanned promotion details are ready. Auto-fill the form with scanned data? (This will overwrite current fields)")) {
                    applyData();
                }
            } else {
                applyData();
            }
            cloneStore.set(null);
        }
    });
  });

  onDestroy(() => {
    if (unsubscribeClone) unsubscribeClone();
    if (unsubscribeScan) unsubscribeScan();
  });
  
  // --- Event Handlers for Modals ---
  function handleBankConfirm(event) { banks = event.detail.selected; showBankModal = false; }
  function handleCreditCardConfirm(event) { creditCards = event.detail.selected; showCreditCardModal = false; }
  function handlePaymentMethodConfirm(event) { methodsOfSpending = event.detail.selected; showPaymentMethodModal = false; }
  function handleCategoryConfirm(event) { categories = event.detail.selected; showCategoryModal = false; }
  function handleStoreConfirm(event) {
      physicalStores = event.detail.selected;
      showStoreModal = false;
  }

  function handlePlaceConfirm(event) {
      places = event.detail.selected;
      showPlaceModal = false;
  }

  // --- Reward Scheme Logic (Frequency) ---
  function addRule() {
    rewardScheme = [
      ...rewardScheme,
      { frequency: 1, rewardType: 'Percentage', percentage: 0, maxRewardAmount: 9999, fixedAmount: 0 }
    ];
  }
  function removeRule(index) {
    rewardScheme = rewardScheme.filter((_, i) => i !== index);
  }

  // --- SCHEDULING FUNCTIONS ---
    function toggleDay(dayId) {
        if (eligibleDaysOfWeek.includes(dayId)) {
            eligibleDaysOfWeek = eligibleDaysOfWeek.filter(d => d !== dayId);
        } else {
            eligibleDaysOfWeek = [...eligibleDaysOfWeek, dayId];
        }
    }

    function addEligibleDate() {
        if (!tempDate) return;
        
        if (eligibleDates.includes(tempDate)) {
            alert("This date is already added.");
            return;
        }

        // VALIDATION: Ensure specific date is within the promotion period
        if (startDate && endDate) {
            const checkDate = new Date(tempDate);
            // Zero out time components for fair comparison
            checkDate.setHours(0,0,0,0);
            
            const start = new Date(startDate);
            start.setHours(0,0,0,0);
            
            const end = new Date(endDate);
            end.setHours(23,59,59,999);

            if (checkDate < start || checkDate > end) {
                alert("Specific date must fall within the Start Date and End Date of the promotion.");
                return;
            }
        } else {
            alert("Please set a Start Date and End Date first.");
            return;
        }

        eligibleDates = [...eligibleDates, tempDate];
        tempDate = ''; // Reset input
    }

    function removeEligibleDate(dateToRemove) {
        eligibleDates = eligibleDates.filter(date => date !== dateToRemove);
    }

    function addIneligibleDate() {
        if (!tempIneligibleDate) return;
        
        if (ineligibleDates.includes(tempIneligibleDate)) {
            alert("This date is already added.");
            return;
        }

        // VALIDATION: Ensure specific date is within the promotion period
        if (startDate && endDate) {
            const checkDate = new Date(tempIneligibleDate);
            checkDate.setHours(0,0,0,0);
            
            const start = new Date(startDate);
            start.setHours(0,0,0,0);
            
            const end = new Date(endDate);
            end.setHours(23,59,59,999);

            if (checkDate < start || checkDate > end) {
                alert("Ineligible date must fall within the Start Date and End Date of the promotion.");
                return;
            }
        } else {
            alert("Please set a Start Date and End Date first.");
            return;
        }

        ineligibleDates = [...ineligibleDates, tempIneligibleDate];
        tempIneligibleDate = ''; // Reset input
    }

    function removeIneligibleDate(dateToRemove) {
        ineligibleDates = ineligibleDates.filter(date => date !== dateToRemove);
    }

  function addTier() {
    rewardTiers = [
      ...rewardTiers,
      { condition: '', rewardType: 'Percentage', percentage: 0, fixedAmount: 0, unitOfMeasure: 'Points', topupAmount: 0 }
    ];
  }
  function removeTier(index) {
    rewardTiers = rewardTiers.filter((_, i) => i !== index);
  }

  // --- Reactive Calculations ---
  $: {
    const orig = parseFloat(originalPrice);
    const disc = parseFloat(discountPrice);
    if (orig > 0 && disc >= 0 && orig > disc) {
      const reward = orig - disc;
      const percent = (reward / orig) * 100;
      maximumReward = reward.toFixed(2);
      maximumRewardPercentage = percent.toFixed(2);
    } else {
      if (!originalPrice && !discountPrice) {
          // Only clear if inputs are empty to allow manual override
      }
    }
  }

  async function handleSubmit() {
    isSubmitting = true;
    error = '';

    if (!name || !startDate || !endDate || !description) {
      error = 'Name, Start Date, End Date, and Description are required.';
      isSubmitting = false; return;
    }
     if (requireRegistration && !registrationDetail.trim()) {
      error = "Registration Detail is required when registration is checked.";
      isSubmitting = false; return;
    }
    if (isOnline && !onlineShops.trim()) {
        error = "A list of online shops is required when 'Available Online' is checked.";
        isSubmitting = false; return;
    }

    // --- FIX: Force UTC Date Construction ---
    // Instead of new Date(startDate).toISOString(), which converts local to UTC (potentially shifting the day),
    // we simply append 'T00:00:00Z' to the date string.
    // This tells the backend "This IS the UTC date".
    const startDateUTC = `${startDate}T00:00:00Z`;
    // For end date, we usually want the END of that day, so we can use 23:59:59
    const endDateUTC = `${endDate}T23:59:59Z`;

    const payload = {
      name,
      description,
      cloneFromPromo,
      brand,
      promoCode,
      startDate: startDateUTC,
      endDate: endDateUTC,
      eligibleDaysOfWeek, // NEW
      eligibleDates,       // NEW
      ineligibleDates,     // NEW
      categories,
      banks,
      creditCards,
      methodsOfSpending,
      requireRegistration,
      isOnline,
      isInStore,
      createdByUserId: currentUser?.idTokenClaims?.oid,
      createdByUserName: userProfile?.displayName || currentUser?.name, 
      
      // Frequency Rules
      rewardScheme: rewardScheme.map(rule => ({
        frequency: parseInt(rule.frequency) || 1,
        rewardType: rule.rewardType,
        fixedAmount: rule.rewardType === 'Fixed' ? parseFloat(rule.fixedAmount) || 0 : null,
        percentage: rule.rewardType === 'Percentage' ? parseFloat(rule.percentage) || 0 : null,
        maxRewardAmount: rule.rewardType === 'Percentage' ? parseFloat(rule.maxRewardAmount) || 0 : null,
      })),

      // Condition Tiers
      rewardTiers: rewardTiers.map(t => ({
          condition: t.condition,
          rewardType: t.rewardType,
          fixedAmount: t.rewardType === 'Fixed' ? parseFloat(t.fixedAmount) || 0 : null,
          percentage: t.rewardType === 'Percentage' ? parseFloat(t.percentage) || 0 : null,
          unitOfMeasure: t.rewardType === 'Fixed' ? t.unitOfMeasure : null,
          topupAmount: t.topupAmount ? parseFloat(t.topupAmount) || 0 : null
      })),
      
      // Price fields
      originalPrice: originalPrice ? parseFloat(originalPrice) : null,
      discountPrice: discountPrice ? parseFloat(discountPrice) : null,
      maximumReward: maximumReward ? parseFloat(maximumReward) : null,
      maximumRewardPercentage: maximumRewardPercentage ? parseFloat(maximumRewardPercentage) : null,
      
      // Pass country code
      countryCode: $selectedCountry
    };

    // Add optional fields...
    if (hyperlink.trim()) payload.hyperlink = hyperlink;
    if (isOnline) payload.onlineShops = onlineShops.split(',').map(s => s.trim()).filter(Boolean);
    
    //if (isInStore) payload.physicalStores = physicalStores.split(',').map(s => s.trim()).filter(Boolean);
    if (isInStore) payload.physicalStores = physicalStores;
    if (isInStore) payload.places = places;

    if (requireRegistration) payload.registrationDetail = registrationDetail;
    if (includeSelectedMember) payload.selectedMember = selectedMember;
    if (minimumSpending) payload.minimumSpending = parseFloat(minimumSpending);
    if (targetNumTransactions) payload.targetNumTransactions = parseInt(targetNumTransactions, 10);
    if (minimumTargetAccumulatedSpending) payload.minimumTargetAccumulatedSpending = parseFloat(minimumTargetAccumulatedSpending);
    if (maximumTargetNumTransactions) payload.maximumTargetNumTransactions = parseInt(maximumTargetNumTransactions, 10);
    if (maximumTargetAccumulatedSpending) payload.maximumTargetAccumulatedSpending = parseFloat(maximumTargetAccumulatedSpending);
    
    try {
      const response = await authFetch(getApiUrl("/promotions"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Failed to add promotion.");
      }
      
      navigate("/", { replace: true });
    } catch (e) {
      error = e.message;
    } finally {
      isSubmitting = false;
    }
  }
</script>

<BankSelectorModal bind:showModal={showBankModal} preSelectedBanks={banks} on:confirm={handleBankConfirm} on:close={() => showBankModal = false} />
<CreditCardSelectorModal bind:showModal={showCreditCardModal} preSelectedCards={creditCards} on:confirm={handleCreditCardConfirm} on:close={() => showCreditCardModal = false} />
<PaymentMethodSelectorModal bind:showModal={showPaymentMethodModal} preSelectedMethods={methodsOfSpending} on:confirm={handlePaymentMethodConfirm} on:close={() => showPaymentMethodModal = false} />
<CategorySelectorModal bind:showModal={showCategoryModal} preSelectedCategories={categories} on:confirm={handleCategoryConfirm} on:close={() => showCategoryModal = false} />
<StoreSelectorModal 
    bind:showModal={showStoreModal} 
    preSelectedStores={physicalStores} 
    on:confirm={handleStoreConfirm} 
    on:close={() => showStoreModal = false} 
/>
<PlaceSelectorModal bind:showModal={showPlaceModal} preSelectedPlaces={places} on:confirm={handlePlaceConfirm} on:close={() => showPlaceModal = false} />


<div class="form-container">
  <h2>Add a New Promotion</h2>
  
  {#if scanState.isScanning}
    <div class="background-scan-banner">
      <span class="spinner">⏳</span>
      <span>Extracting promotion details in background... The backend process is running, please wait.</span>
    </div>
  {/if}

  <form on:submit|preventDefault={handleSubmit}>
    
    <fieldset>
      <legend>Basic Information (Required)</legend>
      <div class="form-group"><label for="name">Promotion Name</label><input type="text" id="name" bind:value={name} /></div>
      <div class="form-group"><label for="description">Description (max 1000 chars)</label><textarea id="description" rows="4" bind:value={description}></textarea></div>
      <div class="form-group"><label for="name">Brand</label><input type="text" id="brand" bind:value={brand} /></div>
      <div class="form-group"><label for="start-date">Start Date</label><input type="date" id="start-date" bind:value={startDate} /></div>
      <div class="form-group"><label for="end-date">End Date</label><input type="date" id="end-date" bind:value={endDate} /></div>
      <div class="form-group"><label for="promo-code">Promo Code (Optional)</label><input type="text" id="promo-code" bind:value={promoCode} placeholder="e.g. SUMMER20" /></div>
      <div class="form-group">
        <label>Categories</label>
        <div class="selection-display">{categories.join(', ') || 'No categories selected'}</div>
        <button type="button" class="select-btn" on:click={() => showCategoryModal = true}>Select Categories</button>
      </div>
    </fieldset>

    <fieldset class="schedule-fieldset">
        <legend>Special Scheduling (Optional)</legend>
        <p class="help-text">Leave these blank if the promotion is valid <strong>every day</strong> during the promotion period.</p>

        <!-- 1. Eligible Days of Week -->
        <div class="form-group">
            <label>Eligible Days of the Week</label>
            <div class="days-grid">
                {#each daysOfWeekList as day}
                    <label class="day-checkbox">
                        <input type="checkbox" 
                               checked={eligibleDaysOfWeek.includes(day.id)}
                               on:change={() => toggleDay(day.id)} />
                        {day.label}
                    </label>
                {/each}
            </div>
        </div>

        <!-- 2. Specific Eligible Dates -->
        <div class="form-group">
            <label>Specific Eligible Dates</label>
            <div class="date-input-group">
                <input type="date" bind:value={tempDate} />
                <button type="button" class="btn-add-date" on:click={addEligibleDate}>Add Date</button>
            </div>
            
            <!-- Display Selected Dates -->
            {#if eligibleDates.length > 0}
                <div class="tags-container">
                    {#each eligibleDates as date}
                        <span class="tag">
                            📅 {date}
                            <button type="button" class="remove-tag" on:click={() => removeEligibleDate(date)} title="Remove">&times;</button>
                        </span>
                    {/each}
                </div>
            {/if}
        </div>

        <!-- 3. Specific Ineligible Dates -->
        <div class="form-group">
            <label>Specific Ineligible Dates</label>
            <div class="date-input-group">
                <input type="date" bind:value={tempIneligibleDate} />
                <button type="button" class="btn-add-date" on:click={addIneligibleDate}>Add Date</button>
            </div>
            
            <!-- Display Selected Dates -->
            {#if ineligibleDates.length > 0}
                <div class="tags-container">
                    {#each ineligibleDates as date}
                        <span class="tag tag-ineligible">
                            🚫 {date}
                            <button type="button" class="remove-tag" on:click={() => removeIneligibleDate(date)} title="Remove">&times;</button>
                        </span>
                    {/each}
                </div>
            {/if}
        </div>
    </fieldset>

    <fieldset>
      <legend>Availability</legend>
      <div class="checkbox-group"><input type="checkbox" id="is-online" bind:checked={isOnline} /><label for="is-online">Available Online?</label></div>
      {#if isOnline}
        <div class="form-group conditional"><label for="online-shops">Online Shops (comma-separated)</label><input type="text" id="online-shops" bind:value={onlineShops} /></div>
      {/if}
      <div class="checkbox-group"><input type="checkbox" id="is-in-store" bind:checked={isInStore} /><label for="is-in-store">Available In-Store?</label></div>
      <!-- {#if isInStore}
        <div class="form-group conditional"><label for="physical-stores">Physical Stores (comma-separated)</label><input type="text" id="physical-stores" bind:value={physicalStores} /></div>
      {/if} -->

      {#if isInStore}
          <div class="form-group conditional">
              <label>Physical Stores</label>
              <div class="selection-display">
                  {physicalStores.length > 0 ? physicalStores.join(', ') : 'No stores selected'}
              </div>
              <button type="button" class="select-btn" on:click={() => showStoreModal = true}>Select Stores</button>
          </div>
          <div class="form-group conditional">
              <label>Places (Malls/Districts)</label>
              <div class="selection-display">
                  {places.length > 0 ? places.join(', ') : 'No places selected'}
              </div>
              <button type="button" class="select-btn" on:click={() => showPlaceModal = true}>Select Places</button>
          </div>
      {/if}
    </fieldset>
    
    <!-- Payment Options Group -->
    <fieldset>
      <legend>Payment Options</legend>
      <div class="form-group">
        <label>Participating Banks</label>
        <div class="selection-display">{banks.join(', ') || 'No banks selected'}</div>
        <button type="button" class="select-btn" on:click={() => showBankModal = true}>Select Banks</button>
      </div>
      <div class="form-group">
        <label>Eligible Credit Cards</label>
        <div class="selection-display">{creditCards.join(', ') || 'No credit cards selected'}</div>
        <button type="button" class="select-btn" on:click={() => showCreditCardModal = true}>Select Credit Cards</button>
      </div>
      <div class="form-group">
        <label>Methods of Spending</label>
        <div class="selection-display">{methodsOfSpending.join(', ') || 'No methods selected'}</div>
        <button type="button" class="select-btn" on:click={() => showPaymentMethodModal = true}>Select Methods</button>
      </div>
    </fieldset>

    <!-- Spending & Achievement Rules Group -->
    <fieldset>
        <legend>Spending & Achievement Rules</legend>
        <div class="form-group"><label for="minimum-spending">Minimum Spending Per Transaction</label><input type="number" step="0.01" min="0" id="minimum-spending" bind:value={minimumSpending} placeholder="e.g., 500.00"/></div>
        <div class="form-group"><label for="target-num-transactions">Target Number of Transactions</label><input type="number" min="0" id="target-num-transactions" bind:value={targetNumTransactions} placeholder="e.g., 5" /></div>
        <div class="form-group"><label for="min-target-spending">Minimum Target Accumulated Spending</label><input type="number" step="0.01" min="0" id="min-target-spending" bind:value={minimumTargetAccumulatedSpending} placeholder="e.g., 10000.00" /></div>
        <div class="form-group"><label for="maximum-target-num-transactions">Maximum Target Number of Transactions</label><input type="number" min="0" id="maximum-target-num-transactions" bind:value={maximumTargetNumTransactions} placeholder="e.g., 10" /></div>
        <div class="form-group"><label for="max-target-spending">Maximum Target Accumulated Spending</label><input type="number" step="0.01" min="0" id="max-target-spending" bind:value={maximumTargetAccumulatedSpending} placeholder="e.g., 20000.00" /></div>
    </fieldset>

    <fieldset>
        <legend>Simple Price Reward (Optional)</legend>
        <p>Use this for simple "Was $X, Now $Y" deals.</p>
        <div class="form-group">
            <label for="original-price">Original Price</label>
            <input type="number" step="0.01" min="0" id="original-price" bind:value={originalPrice} />
        </div>
        <div class="form-group">
            <label for="discount-price">Discount Price</label>
            <input type="number" step="0.01" min="0" id="discount-price" bind:value={discountPrice} />
        </div>
        <div class="form-group">
            <label for="maximum-reward">Maximum Reward</label>
            <input type="number" step="0.01" min="0" id="maximum-reward" bind:value={maximumReward} placeholder="Calculated automatically" />
        </div>
        <div class="form-group">
            <label for="maximum-reward-percentage">Maximum Reward Percentage (%)</label>
            <input type="number" step="0.01" min="0" id="maximum-reward-percentage" bind:value={maximumRewardPercentage} placeholder="Calculated automatically" />
        </div>
    </fieldset>
    
    <!-- Special Reward Tiers (New) -->
    <fieldset>
      <legend>Special Reward Tiers (Optional)</legend>
      <p>Use this for conditional rewards (e.g. "Spend $5000 to get 500 Miles").</p>
      {#if rewardTiers.length === 0}
        <p class="no-rules-text">No reward tiers defined.</p>
      {/if}
      {#each rewardTiers as tier, index}
        <div class="rule-box tier-box">
          <div class="rule-header">
            <span>Tier {index + 1}</span>
            <button type="button" class="remove-btn" on:click={() => removeTier(index)}>Remove</button>
          </div>
          <div class="form-group">
              <label>Condition</label>
              <input type="text" bind:value={tier.condition} placeholder="e.g. Spend over $5000" />
          </div>
          <div class="form-group">
            <label>Reward Type</label>
            <select bind:value={tier.rewardType}>
              <option value="Percentage">Percentage</option>
              <option value="Fixed">Fixed Amount</option>
            </select>
          </div>
          <div class="form-group">
            <label>Topup Amount</label>
            <input type="number" step="0.01" min="0" bind:value={tier.topupAmount} placeholder="e.g. 50.00" />
          </div>
          {#if tier.rewardType === 'Percentage'}
            <div class="form-group conditional">
              <label>Percentage (%)</label>
              <input type="number" step="0.01" min="0" bind:value={tier.percentage} />
            </div>
          {:else if tier.rewardType === 'Fixed'}
            <div class="row">
                <div class="form-group conditional">
                    <label>Amount</label>
                    <input type="number" step="0.01" min="0" bind:value={tier.fixedAmount} />
                </div>
                <div class="form-group conditional">
                    <label>Unit</label>
                    <input type="text" bind:value={tier.unitOfMeasure} placeholder="Miles/Points" />
                </div>
            </div>
          {/if}
        </div>
      {/each}
      <button type="button" class="add-rule-btn" on:click={addTier}>+ Add Reward Tier</button>
    </fieldset>
    
    <fieldset>
      <legend>Advanced Reward Scheme (Optional)</legend>
      <p>Use this for frequency rules (e.g. "Every 4th transaction gets $10 back").</p>
      {#if rewardScheme.length === 0}
        <p class="no-rules-text">No reward rules defined.</p>
      {/if}
      {#each rewardScheme as rule, index}
        <div class="rule-box">
          <div class="rule-header">
            <span>Rule {index + 1}</span>
            <button type="button" class="remove-btn" on:click={() => removeRule(index)}>Remove</button>
          </div>
          <div class="form-group">
            <label>Frequency: Every</label>
            <input type="number" min="1" bind:value={rule.frequency} />
            <span>eligible transaction</span>
          </div>
          <div class="form-group">
            <label>Reward Type:</label>
            <select bind:value={rule.rewardType}>
              <option value="Percentage">Percentage</option>
              <option value="Fixed">Fixed Amount</option>
            </select>
          </div>
          {#if rule.rewardType === 'Percentage'}
            <div class="form-group conditional">
              <label>Percentage (%)</label>
              <input type="number" step="0.01" min="0" bind:value={rule.percentage} />
            </div>
            <div class="form-group conditional">
              <label>Max Reward Amount</label>
              <input type="number" step="0.01" min="0" bind:value={rule.maxRewardAmount} />
            </div>
          {:else if rule.rewardType === 'Fixed'}
            <div class="form-group conditional">
              <label>Fixed Amount</label>
              <input type="number" step="0.01" min="0" bind:value={rule.fixedAmount} />
            </div>
          {/if}
        </div>
      {/each}
      <button type="button" class="add-rule-btn" on:click={addRule}>+ Add Reward Rule</button>
    </fieldset>

    <fieldset>
        <legend>Registration & Membership</legend>
        <div class="checkbox-group"><input type="checkbox" id="require-registration" bind:checked={requireRegistration} /><label for="require-registration">Requires Registration?</label></div>
        {#if requireRegistration}
            <div class="form-group conditional"><label for="registration-detail">Registration Detail (Required)</label><textarea id="registration-detail" bind:value={registrationDetail}></textarea></div>
        {/if}
        <div class="checkbox-group"><input type="checkbox" id="include-selected-member" bind:checked={includeSelectedMember} /><label for="include-selected-member">Specify Selected Member Status?</label></div>
        {#if includeSelectedMember}
            <div class="checkbox-group conditional"><input type="checkbox" id="selected-member" bind:checked={selectedMember} /><label for="selected-member">For Selected Members Only?</label></div>
        {/if}
    </fieldset>

    <fieldset>
      <legend>Additional Details</legend>
      <div class="form-group"><label for="hyperlink">Hyperlink</label><input type="url" id="hyperlink" bind:value={hyperlink} placeholder="https://example.com" /></div>
    </fieldset>

    <button type="submit" disabled={isSubmitting}>
      {#if isSubmitting} Submitting... {:else} Add Promotion {/if}
    </button>
    {#if error} <p class="error">{error}</p> {/if}
  </form>
</div>

<style>
  .form-container { max-width: 700px; margin: 2rem auto; }
  form { display: flex; flex-direction: column; gap: 1rem; }
  fieldset { border: 1px solid #ddd; border-radius: 8px; padding: 1.5rem; margin-bottom: 1rem; }
  legend { font-weight: bold; color: #0056b3; padding: 0 0.5rem; }
  .form-group { margin-bottom: 1rem; }
  label { display: block; margin-bottom: 0.5rem; font-weight: 500; }
  input, textarea, select { width: 100%; padding: 0.75rem; border: 1px solid #ccc; border-radius: 4px; font-size: 1rem; box-sizing: border-box; font-family: inherit; }
  button { padding: 0.75rem; background-color: #007bff; color: white; border: none; border-radius: 4px; font-size: 1.1rem; cursor: pointer; }
  button:disabled { background-color: #aaa; }
  .error { color: red; text-align: center; }
  .checkbox-group { display: flex; align-items: center; gap: 0.5rem; }
  .checkbox-group input { width: auto; }
  .conditional { margin-left: 2rem; margin-top: 1rem; border-left: 3px solid #007bff; padding-left: 1rem; }
  .selection-display {
    min-height: 2rem; padding: 0.5rem; border: 1px solid #eee; border-radius: 4px;
    background-color: #f8f9fa; margin-bottom: 0.5rem; color: #666;
  }
  .select-btn {
    background-color: #6c757d; font-size: 0.9rem; padding: 0.5rem 1rem; margin-top: 0;
  }
  .form-actions {
    display: flex; justify-content: flex-end; gap: 1rem; margin-top: 1rem;
  }
  .clone-btn {
    background-color: #17a2b8;
  }
  hr {
    border: none;
    border-top: 1px solid #eee;
    margin: 1.5rem 0;
  }
  .rule-box { border: 1px dashed #ccc; padding: 1rem; border-radius: 8px; margin-bottom: 1rem; }
  .tier-box { border-color: #6610f2; background-color: #fcf9ff; }
  .rule-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
  .rule-header span { font-weight: bold; }
  .remove-btn { background-color: #dc3545; font-size: 0.8rem; padding: 4px 8px; color: white; border: none; border-radius: 4px; cursor: pointer; }
  .add-rule-btn { background-color: #28a745; margin-top: 1rem; color: white; border: none; border-radius: 4px; cursor: pointer; padding: 0.75rem; font-size: 1.1rem; }
  .no-rules-text { color: #6c757d; text-align: center; }
  .row { display: flex; gap: 1rem; }



    .schedule-fieldset {
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 1.5rem;
        margin-bottom: 1.5rem;
        background-color: #f8fafc;
    }

    .schedule-fieldset legend {
        font-weight: bold;
        color: #2d3748;
        padding: 0 0.5rem;
        background: #fff;
        border: 1px solid #e2e8f0;
        border-radius: 4px;
    }

    .days-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
        gap: 0.5rem;
        margin-top: 0.5rem;
    }

    .day-checkbox {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.95rem;
        cursor: pointer;
        background: #fff;
        padding: 0.5rem;
        border: 1px solid #cbd5e1;
        border-radius: 4px;
        transition: background-color 0.2s;
    }

    .day-checkbox:hover {
        background-color: #f1f5f9;
    }

    .date-input-group {
        display: flex;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
    }

    .date-input-group input {
        flex: 1;
    }

    .btn-add-date {
        padding: 0.5rem 1rem;
        background-color: #4a5568;
        color: white;
        border: none;
        border-radius: 4px;
        font-weight: 500;
        cursor: pointer;
    }

    .btn-add-date:hover {
        background-color: #2d3748;
    }

    .tags-container {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 0.5rem;
    }

    .tag {
        display: inline-flex;
        align-items: center;
        background-color: #ebf8ff;
        color: #2b6cb0;
        padding: 0.4rem 0.8rem;
        border-radius: 20px;
        font-size: 0.9rem;
        font-weight: 600;
        border: 1px solid #bee3f8;
    }

    .remove-tag {
        background: transparent;
        border: none;
        color: #e53e3e;
        font-size: 1.2rem;
        margin-left: 0.5rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        padding: 0;
        line-height: 1;
    }

    .remove-tag:hover {
        color: #9b2c2c;
    }


    .tag-ineligible {
        background-color: #fff5f5;
        color: #e53e3e;
        border: 1px solid #fed7d7;
    }

    .background-scan-banner {
        background-color: #e3f2fd;
        color: #0d47a1;
        padding: 0.75rem 1rem;
        border-radius: 6px;
        margin-bottom: 1.5rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-weight: 500;
        border: 1px solid #bbdefb;
        animation: pulse 2s infinite ease-in-out;
    }

    @keyframes pulse {
        0% { opacity: 0.8; }
        50% { opacity: 1; }
        100% { opacity: 0.8; }
    }

    .spinner {
        display: inline-block;
        animation: spin 2s infinite linear;
    }

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

</style>